import React, { useState } from 'react';
import { Download, Tag, MoreVertical, Trash2, Eye, Check, Cloud, ExternalLink, FolderInput, FolderOutput, Copy, Hash, MessageSquare } from 'lucide-react';
import FavoriteButton from './FavoriteButton';
import { formatDate } from '../utils/helpers';

/**
 * LibraryItemCard Component
 * Displays a library item (casting, location, or creative) in a card format
 * Supports drag functionality for moving items to folders
 *
 * @param {Object} props
 * @param {Object} props.item - The library item data
 * @param {function} props.onFavoriteToggle - Callback when favorite is toggled
 * @param {function} [props.onTagClick] - Callback when a tag is clicked
 * @param {function} [props.onSelect] - Callback when item is selected (in selection mode)
 * @param {function} [props.onDelete] - Callback to delete the item
 * @param {function} [props.onView] - Callback to view item details
 * @param {function} [props.onMoveToFolder] - Callback to move item to a folder
 * @param {function} [props.onRemoveFromFolder] - Callback to remove item from current folder
 * @param {boolean} [props.isSelected=false] - Whether the item is selected
 * @param {boolean} [props.selectable=false] - Whether the item can be selected
 * @param {boolean} [props.compact=false] - Whether to use compact layout
 * @param {boolean} [props.isInFolder=false] - Whether the item is currently in a folder
 * @param {boolean} [props.draggable=true] - Whether the item can be dragged
 * @param {function} [props.onDragStart] - Callback when drag starts
 * @param {function} [props.onDragEnd] - Callback when drag ends
 * @param {Set} [props.selectedItemIds] - Set of selected item IDs for bulk drag
 */
const LibraryItemCard = ({
  item,
  onFavoriteToggle,
  onTagClick,
  onSelect,
  onDelete,
  onView,
  onMoveToFolder,
  onRemoveFromFolder,
  isSelected = false,
  selectable = false,
  compact = false,
  isInFolder = false,
  draggable = true,
  onDragStart,
  onDragEnd,
  selectedItemIds
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [copiedField, setCopiedField] = useState(null); // 'caption' | 'hashtags' | 'all'

  // Get caption from various possible locations
  const caption = item.metadata?.caption || item.description || null;

  // Get hashtags from various possible locations
  const hashtags = item.metadata?.hashtags || item.tags || [];

  // Format hashtags with # symbol for display
  const formattedHashtags = Array.isArray(hashtags)
    ? hashtags.map(tag => tag.startsWith('#') ? tag : `#${tag}`).join(' ')
    : '';

  // Copy to clipboard helper
  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Copy all post content (caption + hashtags)
  const copyAllPostContent = (e) => {
    e.stopPropagation();
    const allContent = [caption, formattedHashtags].filter(Boolean).join('\n\n');
    copyToClipboard(allContent, 'all');
  };

  const handleCardClick = (e) => {
    if (selectable && onSelect) {
      onSelect(item);
    } else if (onView) {
      onView(item);
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!item.imageUrl) return;

    try {
      const response = await fetch(item.imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${item.title || item.name || 'image'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      window.open(item.imageUrl, '_blank');
    }
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onDelete) {
      onDelete(item);
    }
  };

  const handleViewClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onView) {
      onView(item);
    }
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setShowMenu(false);
    if (showMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showMenu]);

  // Drag handlers
  const handleDragStart = (e) => {
    // If this item is selected and there are multiple selections, drag all selected items
    const hasMultipleSelected = selectedItemIds && selectedItemIds.size > 1 && isSelected;

    if (hasMultipleSelected) {
      e.dataTransfer.setData('application/json', JSON.stringify({
        type: 'items',
        itemIds: Array.from(selectedItemIds),
        itemCount: selectedItemIds.size
      }));
    } else {
      e.dataTransfer.setData('application/json', JSON.stringify({
        type: 'item',
        itemId: item.id,
        itemTitle: item.title || item.name || 'Untitled'
      }));
    }
    e.dataTransfer.effectAllowed = 'move';
    if (onDragStart) onDragStart(item);
  };

  const handleDragEnd = (e) => {
    if (onDragEnd) onDragEnd();
  };

  // Allow dragging when: not in selection mode, OR when selected (to drag selection)
  const canDrag = draggable && (!selectable || isSelected);

  return (
    <div
      className="card masonry-item"
      onClick={handleCardClick}
      draggable={canDrag}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={{
        padding: compact ? 'var(--space-2)' : 'var(--space-4)',
        cursor: selectable ? 'pointer' : onView ? 'pointer' : draggable ? 'grab' : 'default',
        position: 'relative',
        transition: 'all var(--transition-fast)',
        border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--color-border-light)',
        boxShadow: isSelected ? '0 0 0 3px var(--color-primary-light)' : undefined,
        zIndex: showMenu ? 1000 : 'auto'
      }}
    >
      {/* Selection indicator */}
      {selectable && isSelected && (
        <div
          style={{
            position: 'absolute',
            top: 'var(--space-2)',
            left: 'var(--space-2)',
            background: 'var(--color-primary)',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}
        >
          <Check size={14} color="white" />
        </div>
      )}

      {/* Favorite button */}
      <div
        style={{
          position: 'absolute',
          top: 'var(--space-4)',
          right: 'var(--space-4)',
          zIndex: 10
        }}
      >
        <FavoriteButton
          isFavorite={item.isFavorite}
          onToggle={() => onFavoriteToggle && onFavoriteToggle(item)}
        />
      </div>

      {/* Image */}
      <div
        className="checkerboard-bg"
        style={{
          width: '100%',
          aspectRatio: item.type === 'casting' ? '3/4' : item.type === 'location' ? '16/9' : undefined,
          borderRadius: 'var(--radius-lg)',
          marginBottom: compact ? 'var(--space-2)' : 'var(--space-3)',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {item.imageUrl && !imageError ? (
          <img
            src={item.imageUrl}
            alt={item.title || item.name || 'Library item'}
            onError={() => setImageError(true)}
            draggable={false}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              transition: 'transform var(--transition-fast)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text-tertiary)',
              fontSize: 'var(--font-size-3xl)'
            }}
          >
            {(item.title || item.name || '?').charAt(0).toUpperCase()}
          </div>
        )}

        {/* Type badge */}
        <span
          className="badge badge-neutral"
          style={{
            position: 'absolute',
            bottom: 'var(--space-2)',
            left: 'var(--space-2)',
            fontSize: '10px',
            textTransform: 'capitalize'
          }}
        >
          {item.type === 'carousel' && item.carouselImages?.length
            ? `Carousel (${item.carouselImages.length} cards)`
            : item.type === 'ad_creative' ? 'Ad Creative' : item.type}
        </span>

        {/* OneDrive badge */}
        {item.sourceType === 'onedrive' && (
          <span
            style={{
              position: 'absolute',
              bottom: 'var(--space-2)',
              right: 'var(--space-2)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              background: 'rgba(0, 120, 212, 0.9)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '10px',
              color: 'white'
            }}
            title="Linked from OneDrive"
          >
            <Cloud size={10} />
          </span>
        )}

        {/* Carousel indicator */}
        {item.type === 'carousel' && item.carouselImages?.length > 1 && (
          <div style={{
            position: 'absolute',
            top: 'var(--space-2)',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '4px'
          }}>
            {item.carouselImages.slice(0, 5).map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: idx === 0 ? 'var(--color-primary)' : 'rgba(255,255,255,0.5)'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Title */}
      <h4 style={{
        fontSize: compact ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
        marginBottom: 'var(--space-1)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {item.title || item.name || 'Untitled'}
      </h4>

      {/* Post Content Section - Caption & Hashtags */}
      {!compact && (caption || formattedHashtags) && (
        <div style={{
          background: 'var(--color-bg-tertiary)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-2)',
          marginBottom: 'var(--space-2)',
          fontSize: 'var(--font-size-xs)'
        }}>
          {/* Section header with copy all button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: caption ? 'var(--space-2)' : 0
          }}>
            <span style={{
              fontSize: '10px',
              fontWeight: '600',
              color: 'var(--color-text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Post Content
            </span>
            <button
              onClick={copyAllPostContent}
              className="btn btn-ghost"
              style={{
                padding: '2px 6px',
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: copiedField === 'all' ? 'var(--color-success)' : 'var(--color-primary)'
              }}
              title="Copy caption & hashtags"
            >
              {copiedField === 'all' ? <Check size={10} /> : <Copy size={10} />}
              {copiedField === 'all' ? 'Copied!' : 'Copy All'}
            </button>
          </div>

          {/* Caption */}
          {caption && (
            <div style={{ marginBottom: formattedHashtags ? 'var(--space-2)' : 0 }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-1)'
              }}>
                <MessageSquare size={12} style={{ color: 'var(--color-text-tertiary)', marginTop: '2px', flexShrink: 0 }} />
                <p style={{
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.4'
                }}>
                  {caption}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(caption, 'caption');
                  }}
                  className="btn btn-ghost"
                  style={{ padding: '2px', flexShrink: 0 }}
                  title="Copy caption"
                >
                  {copiedField === 'caption' ? <Check size={12} color="var(--color-success)" /> : <Copy size={12} />}
                </button>
              </div>
            </div>
          )}

          {/* Hashtags */}
          {formattedHashtags && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-1)'
            }}>
              <Hash size={12} style={{ color: 'var(--color-primary)', marginTop: '2px', flexShrink: 0 }} />
              <p style={{
                color: 'var(--color-primary)',
                margin: 0,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: '1.4',
                wordBreak: 'break-word'
              }}>
                {formattedHashtags}
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(formattedHashtags, 'hashtags');
                }}
                className="btn btn-ghost"
                style={{ padding: '2px', flexShrink: 0 }}
                title="Copy hashtags"
              >
                {copiedField === 'hashtags' ? <Check size={12} color="var(--color-success)" /> : <Copy size={12} />}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tags (only show if no hashtags in post content, to avoid duplication) */}
      {item.tags && item.tags.length > 0 && !formattedHashtags && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-1)',
          marginBottom: 'var(--space-2)'
        }}>
          {item.tags.slice(0, 3).map(tag => (
            <span
              key={tag}
              onClick={(e) => {
                e.stopPropagation();
                if (onTagClick) onTagClick(tag);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                padding: '2px var(--space-2)',
                background: 'var(--color-bg-tertiary)',
                borderRadius: 'var(--radius-full)',
                fontSize: '10px',
                color: 'var(--color-text-secondary)',
                cursor: onTagClick ? 'pointer' : 'default'
              }}
            >
              <Tag size={10} />
              {tag}
            </span>
          ))}
          {item.tags.length > 3 && (
            <span style={{
              fontSize: '10px',
              color: 'var(--color-text-tertiary)',
              padding: '2px var(--space-2)'
            }}>
              +{item.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      {!compact && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 'auto'
        }}>
          <span style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-tertiary)'
          }}>
            {item.createdAt && formatDate(item.createdAt.toDate ? item.createdAt.toDate() : item.createdAt)}
          </span>

          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            {/* Usage count */}
            {item.usageCount > 0 && (
              <span style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)',
                padding: '2px var(--space-2)',
                background: 'var(--color-bg-tertiary)',
                borderRadius: 'var(--radius-full)'
              }}>
                Used {item.usageCount}x
              </span>
            )}

            {/* Download button */}
            {item.imageUrl && (
              <button
                onClick={handleDownload}
                className="btn btn-ghost btn-sm"
                style={{ padding: 'var(--space-1)' }}
                title="Download"
              >
                <Download size={14} />
              </button>
            )}

            {/* More menu */}
            {(onDelete || onView || onMoveToFolder || onRemoveFromFolder) && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={handleMenuClick}
                  className="btn btn-ghost btn-sm"
                  style={{ padding: 'var(--space-1)' }}
                >
                  <MoreVertical size={14} />
                </button>

                {showMenu && (
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: '100%',
                      marginTop: 'var(--space-1)',
                      background: 'var(--color-bg-primary)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-lg)',
                      zIndex: 1001,
                      minWidth: '120px',
                      overflow: 'hidden'
                    }}
                  >
                    {onView && (
                      <button
                        onClick={handleViewClick}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-3)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-primary)',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <Eye size={14} />
                        View Details
                      </button>
                    )}
                    {item.sourceType === 'onedrive' && item.oneDriveMetadata?.webUrl && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          window.open(item.oneDriveMetadata.webUrl, '_blank');
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-3)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)',
                          color: '#0078D4',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <ExternalLink size={14} />
                        Open in OneDrive
                      </button>
                    )}
                    {onMoveToFolder && !isInFolder && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          onMoveToFolder(item);
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-3)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-primary)',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <FolderInput size={14} />
                        Move to Folder
                      </button>
                    )}
                    {onRemoveFromFolder && isInFolder && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(false);
                          onRemoveFromFolder(item);
                        }}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-3)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--color-text-primary)',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <FolderOutput size={14} />
                        Remove from Folder
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={handleDeleteClick}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-3)',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--error-600)',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--error-50)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryItemCard;
